import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { AuditFormData } from "@/pages/AuditFlow";
import AuditProgress from "./AuditProgress";

interface AuditStep1Props {
  data: Partial<AuditFormData>;
  onNext: (data: Partial<AuditFormData>) => void;
  onBack: () => void;
}

export default function AuditStep1({ data, onNext, onBack }: AuditStep1Props) {
  const [industry, setIndustry] = useState(data.industry || "");
  const [companySize, setCompanySize] = useState(data.company_size || "");
  const [monthlyLeads, setMonthlyLeads] = useState(data.monthly_leads || "");
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    setIsValid(!!industry && !!companySize && !!monthlyLeads);
  }, [industry, companySize, monthlyLeads]);

  const handleNext = () => {
    if (isValid) {
      onNext({
        industry,
        company_size: companySize,
        monthly_leads: monthlyLeads,
      });
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <AuditProgress currentStep={1} totalSteps={3} />

      <div className="bg-dark-card rounded-2xl p-8 md:p-12 border border-border/20 mt-8">
        <h2 className="text-3xl font-bold text-foreground mb-3">
          Business Profile
        </h2>
        <p className="text-light-gray/80 mb-8">
          Help us understand your business context
        </p>

        <div className="space-y-6">
          {/* Industry */}
          <div>
            <Label htmlFor="industry" className="text-foreground mb-2 block">
              Industry *
            </Label>
            <Select value={industry} onValueChange={setIndustry}>
              <SelectTrigger id="industry" className="bg-background border-border">
                <SelectValue placeholder="Select your industry" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Logistics">Logistics</SelectItem>
                <SelectItem value="SaaS">SaaS</SelectItem>
                <SelectItem value="Professional Services">
                  Professional Services
                </SelectItem>
                <SelectItem value="Industrial/Trades">
                  Industrial/Trades
                </SelectItem>
                <SelectItem value="Healthcare">Healthcare</SelectItem>
                <SelectItem value="eCommerce">eCommerce</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Company Size */}
          <div>
            <Label htmlFor="company_size" className="text-foreground mb-2 block">
              Company Size *
            </Label>
            <Select value={companySize} onValueChange={setCompanySize}>
              <SelectTrigger id="company_size" className="bg-background border-border">
                <SelectValue placeholder="Select company size" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Solo-10">Solo-10 employees</SelectItem>
                <SelectItem value="11-50">11-50 employees</SelectItem>
                <SelectItem value="51-200">51-200 employees</SelectItem>
                <SelectItem value="201-1000">201-1,000 employees</SelectItem>
                <SelectItem value="1001+">1,001+ employees</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Monthly Leads */}
          <div>
            <Label htmlFor="monthly_leads" className="text-foreground mb-2 block">
              Monthly Inbound Leads *
            </Label>
            <Select value={monthlyLeads} onValueChange={setMonthlyLeads}>
              <SelectTrigger id="monthly_leads" className="bg-background border-border">
                <SelectValue placeholder="Select monthly leads range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="<50">Less than 50</SelectItem>
                <SelectItem value="50-200">50-200</SelectItem>
                <SelectItem value="200-1000">200-1,000</SelectItem>
                <SelectItem value="1000+">1,000+</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-between mt-10">
          <Button
            onClick={onBack}
            variant="outline"
            className="border-border text-foreground hover:bg-dark-card"
          >
            <ArrowLeft className="mr-2 w-4 h-4" />
            Back
          </Button>
          <Button
            onClick={handleNext}
            disabled={!isValid}
            className="bg-cyan hover:bg-cyan-muted text-deep-navy font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}